import get from 'lodash/get';
import store from '../..';
import { GenerateContentValidationStateSegment } from '../../stateSegments/GenerateContentValidationStateSegment';
import { fetchGeneratedContentValidation } from '../../../../services/Content';
import { popoverStateSegment } from '../../stateSegments/popoverStateSegment';
import { contentListStateSegment } from '../../stateSegments/contentListSegment';
import {
  ArrowPosition,
  PopoverVariant,
} from '../../../../components/atoms/Popover';
import evaluateQualityChecks, {
  getNeedsReviewCount,
} from '../../../../utils/evaluateQualityChecks';
import { QcReviewName } from '../../../../types/QualityChecks';
import {
  ApplicableRecommendation,
  ApplicableRecommendationData,
  QualityCheck,
} from '../../../../types/contentValidation';
import { translate } from '../../../../localization';
import { trackQualityChecksOutCome } from '../../../../services/Analytics';
import { getEncodedUserNtid } from '../../helpers/common';
import { ANALYTICS, PAGE_ROUTES } from '../../../../constants/common';

// Helper function to check if user is currently on generate-content route and has content
const isOnGenerateContentRoute = (): boolean => {
  if (typeof window !== 'undefined') {
    const isCorrectRoute =
      window.location.pathname === PAGE_ROUTES.GENERATE_CONTENT;
    const { contentList } = store.get(contentListStateSegment);
    const hasContent = contentList && contentList.length > 0;
    return isCorrectRoute && hasContent;
  }
  return false;
};

export const getGeneratedContentValidations = async ({
  contentId,
  sessionId,
  contentGroupId,
  action,
}: {
  contentId: string;
  sessionId: string;
  contentGroupId: string;
  action: string;
}) => {
  store.set(GenerateContentValidationStateSegment, prev => {
    return {
      ...prev,
      [contentId]: {
        ...prev[contentId],
        isLoading: true,
      },
    };
  });

  const { data, success } = await fetchGeneratedContentValidation({
    contentId,
    sessionId,
    contentGroupId,
    action,
  });

  if (data && success) {
    const currentContentValidation = get(
      data,
      'generatedContentValidation[0]',
      { qualityChecks: [], applicableRecommendations: [] }
    );
    const qualityChecks = get(
      currentContentValidation,
      'qualityChecks',
      []
    ) as QualityCheck[];
    const applicableRecommendations: ApplicableRecommendationData[] = (
      get(
        currentContentValidation,
        'applicableRecommendations',
        []
      ) as ApplicableRecommendation[]
    ).map((el: ApplicableRecommendation) => ({
      ...el,
      isSelected: false,
    }));

    const qualityChecksStatus = evaluateQualityChecks(qualityChecks);
    const needsReviewCount = getNeedsReviewCount(qualityChecks);

    store.set(GenerateContentValidationStateSegment, prev => {
      return {
        ...prev,
        [contentId]: {
          ...prev[contentId],
          error: false,
          errorMessage: '',
          isLoading: false,
          status: qualityChecksStatus,
          needsReviewCount,
          qualityChecks,
          applicableRecommendations,
          retryParams: {
            contentId,
            sessionId,
            contentGroupId,
            action,
          },
          retryCount: 0,
        },
      };
    });

    const getPopoverDetails = (status: QcReviewName) => {
      if (status === QcReviewName.PASSED) {
        return {
          description: 'Click here to see the Quality Checks.',
          title: 'Content Looks Great!',
          variant: PopoverVariant.SUCCESS,
        };
      }

      return {
        description: 'Click here to see the Quality Checks.',
        title: 'Your Content Needs Review',
      };
    };

    const popoverDetails = getPopoverDetails(qualityChecksStatus);

    // Only show popover if user is still on generate-content route
    if (isOnGenerateContentRoute()) {
      store.set(popoverStateSegment, prev => {
        return {
          ...prev,
          showPopover: true,
          variant: popoverDetails.variant || PopoverVariant.DEFAULT,
          arrowPosition: ArrowPosition.LEFT_CENTER,
          title: popoverDetails.title,
          description: popoverDetails.description,
          showCloseButton: true,
          showIcon: true,
          buttonLabel: '',
          qualityChecksStatus,
        };
      });
    }

    const userEncodedNtid = getEncodedUserNtid();

    // Track quality checks outcome analytics based on review status
    // Include recommendations count when content needs review and recommendations are available
    if (needsReviewCount > 0) {
      const recommendationsCount =
        applicableRecommendations.length > 0
          ? applicableRecommendations.length
          : undefined;
      trackQualityChecksOutCome(
        ANALYTICS.QC_OUTCOME_NEEDS_REVIEW_NAME,
        userEncodedNtid,
        recommendationsCount
      );
    } else {
      trackQualityChecksOutCome(
        ANALYTICS.QC_OUTCOME_PASSED_NAME,
        userEncodedNtid
      );
    }
  } else {
    store.set(GenerateContentValidationStateSegment, prev => {
      const contentIdData = prev[contentId] || {}; // Safely access existing data

      return {
        ...prev,
        [contentId]: {
          ...contentIdData, // Spread existing contentId data
          error: true,
          errorMessage: 'Failed to load quality checks',
          isLoading: false,
          needsReviewCount: 0,
          qualityChecks: [],
          applicableRecommendations: [],
          retryParams: {
            contentId,
            sessionId,
            contentGroupId,
            action,
          },
          retryCount: (contentIdData.retryCount || 0) + 1, // Increment retryCount, defaulting to 0 if it doesn't exist
        },
      };
    });

    const validationData = store.get(GenerateContentValidationStateSegment);
    const selectedVaildationData = validationData[contentId] || {};

    if (selectedVaildationData.retryCount > 3) {
      // Only show popover if user is still on generate-content route
      if (isOnGenerateContentRoute()) {
        store.set(popoverStateSegment, prev => {
          return {
            ...prev,
            showPopover: true,
            variant: PopoverVariant.WARNING,
            arrowPosition: ArrowPosition.LEFT_CENTER,
            title: translate('quality_checks_failed_to_load'),
            description: translate(
              'quality_checks_failed_to_load_description_server_down'
            ),
            buttonLabel: '',
            onButtonClick: () => {
              getGeneratedContentValidations({
                contentId,
                sessionId,
                contentGroupId,
                action,
              });
              store.set(popoverStateSegment, prevPropover => {
                return {
                  ...prevPropover,
                  showPopover: false,
                };
              });
            },
            showCloseButton: true,
            showIcon: true,
            qualityChecksStatus: QcReviewName.FAILED,
          };
        });
      }
    } else if (isOnGenerateContentRoute()) {
      store.set(popoverStateSegment, prev => {
        return {
          ...prev,
          showPopover: true,
          variant: PopoverVariant.WARNING,
          arrowPosition: ArrowPosition.LEFT_CENTER,
          title: translate('quality_checks_failed_to_load'),
          description: translate('quality_checks_failed_to_load_description'),
          buttonLabel: translate('common_retry'),
          onButtonClick: () => {
            getGeneratedContentValidations({
              contentId,
              sessionId,
              contentGroupId,
              action,
            });
            store.set(popoverStateSegment, prevPropover => {
              return {
                ...prevPropover,
                showPopover: false,
              };
            });
          },
          showCloseButton: true,
          showIcon: true,
          qualityChecksStatus: QcReviewName.FAILED,
        };
      });
    }
  }
};
